import React, { useState, useEffect } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Calendar, TrendingUp, Users, Activity } from 'lucide-react';
import { format, subDays, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const COLORS = ['#F59E0B', '#3B82F6', '#10B981', '#EF4444', '#8B5CF6'];

const AnalyticsPage: React.FC = () => {
  const { supabase } = useSupabase();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);
  const [signupData, setSignupData] = useState<any[]>([]);
  const [loginData, setLoginData] = useState<any[]>([]);
  const [planDistribution, setPlanDistribution] = useState<any[]>([]);
  const [totalStats, setTotalStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newUsersThisMonth: 0,
    totalPageViews: 0,
  });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      const startDate = startOfDay(subDays(new Date(), dateRange));

      const { data: users, error: usersError } = await supabase
        .from('profiles')
        .select('*')
        .gte('created_at', startDate.toISOString());

      if (usersError) throw usersError;

      const { data: allUsers } = await supabase
        .from('profiles')
        .select('plan, status');

      const signupsByDay: { [key: string]: number } = {};
      users?.forEach((user) => {
        const day = format(new Date(user.created_at), 'dd/MM');
        signupsByDay[day] = (signupsByDay[day] || 0) + 1;
      });

      const signupChartData = Object.entries(signupsByDay).map(([day, count]) => ({
        day,
        cadastros: count,
      }));

      setSignupData(signupChartData);

      const planCounts = allUsers?.reduce((acc: any, user) => {
        const plan = user.plan || 'free';
        acc[plan] = (acc[plan] || 0) + 1;
        return acc;
      }, {});

      const planChartData = Object.entries(planCounts || {}).map(([name, value]) => ({
        name: name === 'free' ? 'Gratuito' : name === 'monthly' ? 'Mensal' : 'Anual',
        value,
      }));

      setPlanDistribution(planChartData);

      const thisMonth = new Date();
      thisMonth.setDate(1);
      thisMonth.setHours(0, 0, 0, 0);

      const { count: newUsersCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thisMonth.toISOString());

      const { count: totalUsersCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      const { count: activeUsersCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      let pageViewsCount = 0;
      try {
        const { count } = await supabase
          .from('page_views')
          .select('*', { count: 'exact', head: true });
        pageViewsCount = count || 0;
      } catch (error) {
        console.log('page_views table not available');
      }

      setTotalStats({
        totalUsers: totalUsersCount || 0,
        activeUsers: activeUsersCount || 0,
        newUsersThisMonth: newUsersCount || 0,
        totalPageViews: pageViewsCount,
      });

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Analytics</h1>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(Number(e.target.value))}
          className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
        >
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total de Usuários</p>
              <p className="text-3xl font-bold text-white mt-2">{totalStats.totalUsers}</p>
            </div>
            <Users className="w-10 h-10 text-amber-400" />
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Usuários Ativos</p>
              <p className="text-3xl font-bold text-green-400 mt-2">{totalStats.activeUsers}</p>
            </div>
            <Activity className="w-10 h-10 text-green-400" />
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Novos Este Mês</p>
              <p className="text-3xl font-bold text-blue-400 mt-2">{totalStats.newUsersThisMonth}</p>
            </div>
            <TrendingUp className="w-10 h-10 text-blue-400" />
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Visualizações</p>
              <p className="text-3xl font-bold text-purple-400 mt-2">{totalStats.totalPageViews}</p>
            </div>
            <Calendar className="w-10 h-10 text-purple-400" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Cadastros por Dia</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={signupData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="day" stroke="#94A3B8" />
              <YAxis stroke="#94A3B8" />
              <Tooltip
                contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px' }}
                labelStyle={{ color: '#F1F5F9' }}
              />
              <Bar dataKey="cadastros" fill="#F59E0B" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Distribuição de Planos</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={planDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {planDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
